// Turn LaTeX in a rubric into the plain maths a teacher writes on the board.
//  $5\frac{1}{4} \div \frac{1}{7}$  →  5 1/4 ÷ 1/7
// Text with no LaTeX is left unchanged, including a price like $5.

const SUP = {
  0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹',
  '+': '⁺', '-': '⁻'
};
const SUB = {
  0: '₀', 1: '₁', 2: '₂', 3: '₃', 4: '₄', 5: '₅', 6: '₆', 7: '₇', 8: '₈', 9: '₉'
};

const SYMBOLS = [
  [/\\times\b/g, '×'],
  [/\\div\b/g, '÷'],
  [/\\pm\b/g, '±'],
  [/\\cdot\b/g, '·'],
  [/\\leqslant\b/g, '≤'],
  [/\\geqslant\b/g, '≥'],
  [/\\leq\b/g, '≤'],
  [/\\geq\b/g, '≥'],
  [/\\le\b/g, '≤'],
  [/\\ge\b/g, '≥'],
  [/\\neq\b/g, '≠'],
  [/\\ne\b/g, '≠'],
  [/\\lt\b/g, '<'],
  [/\\gt\b/g, '>'],
  [/\\approx\b/g, '≈'],
  [/\\infty\b/g, '∞'],
  [/\\degree\b|\\circ\b/g, '°'],
  [/\\rightarrow\b|\\to\b/g, '→'],
  [/\\sqrt\b/g, '√'],
  [/\\pi\b/g, 'π'],
  [/\\theta\b/g, 'θ'],
  [/\\alpha\b/g, 'α'],
  [/\\beta\b/g, 'β'],
  [/\\gamma\b/g, 'γ'],
  [/\\Delta\b/g, 'Δ'],
  [/\\delta\b/g, 'δ'],
  [/\\lambda\b/g, 'λ'],
  [/\\mu\b/g, 'μ'],
  [/\\sin\b/g, 'sin'],
  [/\\cos\b/g, 'cos'],
  [/\\tan\b/g, 'tan'],
  [/\\ln\b/g, 'ln'],
  [/\\log\b/g, 'log']
];

function readBraceGroup(s, start) {
  if (s[start] !== '{') return null;
  let depth = 0;
  for (let i = start; i < s.length; i += 1) {
    if (s[i] === '{') depth += 1;
    else if (s[i] === '}') {
      depth -= 1;
      if (depth === 0) return { inner: s.slice(start + 1, i), end: i + 1 };
    }
  }
  return null;
}

function replaceTwoArg(s, cmd, fmt) {
  const needle = `\\${cmd}`;
  let out = '';
  let i = 0;
  while (i < s.length) {
    const idx = s.indexOf(needle, i);
    if (idx < 0) {
      out += s.slice(i);
      break;
    }
    const after = idx + needle.length;
    if (/[a-zA-Z]/.test(s[after] || '')) {
      out += s.slice(i, after);
      i = after;
      continue;
    }
    out += s.slice(i, idx);
    let p = after;
    while (p < s.length && /\s/.test(s[p])) p += 1;
    const a = readBraceGroup(s, p);
    if (!a) {
      out += needle;
      i = after;
      continue;
    }
    p = a.end;
    while (p < s.length && /\s/.test(s[p])) p += 1;
    const b = readBraceGroup(s, p);
    if (!b) {
      out += s.slice(idx, a.end);
      i = a.end;
      continue;
    }
    out += fmt(a.inner, b.inner, out);
    i = b.end;
  }
  return out;
}

function isSimple(value) {
  return /^[A-Za-z0-9.⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻]+$/.test(String(value || '').trim());
}

function fractionBody(a, b) {
  const A = String(a || '').trim();
  const B = String(b || '').trim();
  const left = isSimple(A) ? A : `(${A})`;
  const right = isSimple(B) ? B : `(${B})`;
  return `${left}/${right}`;
}

function applyFractions(s) {
  let out = s;
  for (let n = 0; n < 6; n += 1) {
    let next = out;
    ['dfrac', 'tfrac', 'frac'].forEach((cmd) => {
      next = replaceTwoArg(next, cmd, (a, b, soFar) => {
        const body = fractionBody(a, b);
        return /\d$/.test(soFar) ? ` ${body}` : body;
      });
    });
    if (next === out) break;
    out = next;
  }
  return out;
}

function applyPowers(s) {
  let out = s.replace(/\^\{([^{}]*)\}/g, (_, inner) => {
    const v = inner.trim();
    if (v && [...v].every((ch) => SUP[ch])) return [...v].map((ch) => SUP[ch]).join('');
    return `^(${v})`;
  });
  out = out.replace(/_\{([^{}]*)\}/g, (_, inner) => {
    const v = inner.trim();
    if (v && [...v].every((ch) => SUB[ch])) return [...v].map((ch) => SUB[ch]).join('');
    return `_(${v})`;
  });
  out = out.replace(/([A-Za-z0-9)\]])\^(\d+)/g, (_, base, digits) => (
    base + [...digits].map((ch) => SUP[ch] || ch).join('')
  ));
  out = out.replace(/([A-Za-z0-9)\]])_(\d+)/g, (_, base, digits) => (
    base + [...digits].map((ch) => SUB[ch] || ch).join('')
  ));
  return out;
}

function hasLatex(text) {
  return /\\[a-zA-Z]/.test(text) || /\$[^$\n]*[\\^_{}][^$\n]*\$/.test(text);
}

// $40,000 + 2,000$ is a sum. $5 and $1,200 are prices and stay as written.
function isWrappedMath(inner) {
  const t = String(inner || '').trim();
  if (!t) return false;
  if (/[\\/^_=<>≤≥±≠≈⁰¹²³⁴⁵⁶⁷⁸⁹πθ/]/.test(t)) return true;
  return /[0-9][^+\-−]*[+\-−][^+\-−]*[0-9]/.test(t);
}

function arrayToLines(block) {
  const body = block
    .replace(/\$\$/g, '')
    .replace(/\\begin\{array\}(?:\s*\{[^{}]*\})?/g, '')
    .replace(/\\end\{array\}/g, '')
    .replace(/\\hline\b/g, '\n---\n')
    .replace(/\\\\/g, '\n')
    .replace(/\\(?:text|mathrm|mathbf)\s*\{([^{}]*)\}/g, '$1');
  return body
    .split('\n')
    .map((line) => line.replace(/[{}]/g, '').trim())
    .filter((line) => line && !/^(?:array|[rlc|]+)$/i.test(line))
    .join('\n');
}

// $$ \begin{array}{rl} 3.98 \\ + 4.39 \\ \hline \end{array} $$ → stacked lines.
function rewriteArrays(s) {
  return s.replace(/\$\$[\s\S]*?\$\$|\\begin\{array\}[\s\S]*?\\end\{array\}/g, (block) => {
    if (!/\\begin\{array\}|\\hline|\\\\/.test(block)) return block.replace(/\$\$/g, '');
    const plain = arrayToLines(block);
    return plain ? `\n${plain}\n` : block.replace(/\$\$/g, '');
  });
}

function normalizeMathGlyphs(value) {
  return String(value || '')
    .replace(/\\\$/g, '$')
    .replace(/\\\|/g, '|')
    .replace(/\uFF04/g, '$')
    .replace(/[∣│｜¦]/g, '|')
    .replace(/[−–—―─━]/g, '-');
}

function isWorkingPiece(inner) {
  const t = normalizeMathGlyphs(inner).trim();
  if (!t) return false;
  if (/[A-Za-z\u0600-\u06FF]/.test(t)) return false;
  return /\d/.test(t) || /^[\s|+\-=._]+$/.test(t);
}

// Stack $195$ $12|2345$ $ -12$ $ ---$ into the sum a teacher writes on the board.
function formatLongDivision(parts) {
  const cleaned = parts.map((p) => String(p).replace(/\s/g, '')).filter(Boolean);
  const bar = cleaned.findIndex((p) => /^\d+\|\d+$/.test(p));
  if (bar < 0) return cleaned.join('\n');
  const [divisor, dividend] = cleaned[bar].split('|');
  const width = dividend.length;
  const gutter = divisor.length + 3;
  const lines = [];
  cleaned.slice(0, bar).forEach((row) => {
    lines.push(/^\d+$/.test(row)
      ? `${' '.repeat(gutter)}${row.padStart(width)}`
      : row);
  });
  lines.push(`${divisor} | ${dividend}`);
  cleaned.slice(bar + 1).forEach((row) => {
    lines.push(/^-{2,}$/.test(row)
      ? `${' '.repeat(gutter)}${'-'.repeat(width)}`
      : `${' '.repeat(gutter)}${row.padStart(width)}`);
  });
  return lines.join('\n');
}

function divisionParts(line) {
  const norm = normalizeMathGlyphs(line);
  if (!norm.includes('$')) return null;
  const bare = norm.replace(/\$/g, ' ');
  const compact = bare.replace(/\s/g, '');
  if (!/\d+\|\d+/.test(compact)) return null;
  const parts = bare.split(/\s+/).filter(Boolean);
  if (!parts.some((p) => /^\d+\|\d+$/.test(p))) return null;
  return parts;
}

function unwrapDollarWorking(s) {
  return s.replace(/(?:\$[^$\n]+\$[ \t]*){2,}/g, (run) => {
    const parts = divisionParts(run);
    if (!parts) return run;
    return `\n\`\`\`\n${formatLongDivision(parts)}\n\`\`\`\n`;
  });
}

// A whole line of $195$ $12|2345$ $−12$ … even when the minus signs are not
// plain hyphens, or the dollars were written as \$.
function rewriteDivisionLines(text) {
  return String(text).split('\n').map((line) => {
    if (line.trim().startsWith('```')) return line;
    const parts = divisionParts(line);
    if (!parts) return line;
    return '```\n' + formatLongDivision(parts) + '\n```';
  }).join('\n');
}

function fencePlainWorking(s) {
  const lines = String(s).split('\n');
  const out = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].trim().startsWith('```')) {
      out.push(lines[i]);
      i += 1;
      while (i < lines.length && lines[i].trim() !== '```') {
        out.push(lines[i]);
        i += 1;
      }
      if (i < lines.length) out.push(lines[i]);
      i += 1;
      continue;
    }
    let j = i;
    const run = [];
    while (j < lines.length && isWorkingPiece(lines[j])) {
      run.push(lines[j]);
      j += 1;
    }
    const compact = run.map((line) => line.replace(/\s/g, '')).filter(Boolean);
    if (compact.length >= 3 && compact.some((p) => /^\d+\|\d+$/.test(p))) {
      out.push('```');
      out.push(formatLongDivision(compact));
      out.push('```');
      i = j;
      continue;
    }
    out.push(lines[i]);
    i += 1;
  }
  return out.join('\n');
}

/**
 * A stacked addition sometimes arrives as "+∨176,923" or "+√04,000".
 * The mark after the plus is not part of the sum.
 */
export function dropStraySumMarks(text) {
  const stray = '[\\u2228\\u221A\\u2713\\u2714\\u02C7\\u22C0\\u22C1Vv]';
  return String(text || '')
    .replace(
      new RegExp(`^([ \\t]*[+\\-−])[ \\t]*${stray}\\u0332?[ \\t]*(\\d[\\d,]*)[ \\t]*$`, 'gm'),
      '$1 $2'
    )
    .replace(
      /^[ \t]*[\u2228\u221A\u2713\u2714\u02C7\u22C0\u22C1][ \t]*(\d[\d,]*)[ \t]*$/gm,
      '+ $1'
    )
    .replace(/([+\-−])[ \t]*\u0332+(?=\d)/g, '$1 ');
}

/** \underline{5} keeps a line under 5. The command is not deleted. */
export function showLatexUnderlines(text) {
  let s = String(text || '');
  const mark = (inner) => [...inner].map((ch) => (/\s/.test(ch) ? ch : `${ch}\u0332`)).join('');
  s = s.replace(/\\underline\s*\{([^{}]*)\}/g, (_, inner) => mark(inner));
  s = s.replace(/<u>([^<]*)<\/u>/gi, (_, inner) => mark(inner));
  s = s.replace(/\$([^$\n]*\u0332[^$\n]*)\$/g, (full, inner) => {
    const bare = inner.replace(/\u0332/g, '').trim();
    return /^[0-9][0-9,]*$/.test(bare) ? inner.trim() : full;
  });
  return s;
}

/** "$8 \\ge 5$)*" is the comparison 8 ≥ 5). The star is not part of the sentence. */
function dropComparisonStars(text) {
  return String(text || '').replace(/(\d\s*[<>≤≥≠=]\s*\d\s*\))\*/g, '$1');
}

/** Heading marks and bold/italic stars, so ### **Q1.** reads as Q1. */
export function cleanPaperDecor(text) {
  let s = String(text || '').replace(/^[ \t]*#{1,6}[ \t]*/gm, '');
  for (let n = 0; n < 4; n += 1) {
    const next = s
      .replace(/\*\*\*([^*\n]+)\*\*\*/g, '$1')
      .replace(/\*\*([^*\n]+)\*\*/g, '$1')
      .replace(/(^|[^\w*])\*([^*\n]+)\*(?=[^\w*]|$)/g, '$1$2')
      .replace(/__([^_\n]+)__/g, '$1');
    if (next === s) break;
    s = next;
  }
  return s.replace(/^[ \t]*\*{1,3}[ \t]+/gm, '');
}

/** 2345 ÷ 12 written as the bracket the teacher uses, not one crossed line. */
function formatBracketDivision(dividend, divisor) {
  const digits = String(dividend).replace(/\D/g, '').split('').map(Number);
  const div = Number(String(divisor).replace(/\D/g, ''));
  if (!digits.length || !div) return '';
  let i = 0;
  let cur = 0;
  const quot = [];
  const steps = [];
  while (i < digits.length) {
    cur = cur * 10 + digits[i];
    i += 1;
    if (cur < div && quot.length === 0 && i < digits.length) continue;
    const q = Math.floor(cur / div);
    const prod = q * div;
    steps.push({ prod, q, rem: cur - prod, bring: i < digits.length ? digits[i] : null });
    quot.push(String(q));
    cur -= prod;
  }
  const width = digits.length;
  const gutter = String(div).length + 3;
  const pad = (value) => ' '.repeat(gutter) + String(value).padStart(width);
  const lines = [
    `${pad(quot.join('') || '0')} <-- Quotient`,
    `${div} ) ${digits.join('')}`
  ];
  steps.forEach((step) => {
    lines.push(`${pad(`- ${step.prod}`)} (${div} × ${step.q} = ${step.prod})`);
    lines.push(pad('-'.repeat(Math.max(width, String(step.prod).length))));
    if (step.bring != null) {
      lines.push(`${pad(String(step.rem) + step.bring)} (Bring down ${step.bring})`);
    }
  });
  lines.push(`${pad(cur)} <-- Remainder`);
  return `\`\`\`\n${lines.join('\n')}\n\`\`\``;
}

/** "23 ÷ 12 = 1 (remainder 11) Bring down 4 → …" becomes the bracket stack. */
function stackCrossedDivision(text) {
  return String(text || '').split('\n').map((line) => {
    if (!/÷/.test(line) || !/bring down/i.test(line)) return line;
    const lead = line.match(/([0-9][0-9,]*)\s*÷\s*([0-9][0-9,]*)/);
    if (!lead) return line;
    const stacked = formatBracketDivision(lead[1], lead[2]);
    if (!stacked) return line;
    const prefix = line.slice(0, lead.index).replace(/:\s*$/, '').trim();
    return prefix ? `${prefix}\n${stacked}` : stacked;
  }).join('\n');
}

/**
 * A broken stack such as "700,124 + 176,923 700,124 / - 176,923 / 877,047"
 * is one equation: 700,124 + 176,923 = 877,047. The minus line is not a bullet.
 */
function clarifyWorkedSums(text) {
  const num = '[0-9][0-9,]*';
  const lines = String(text || '').split('\n');
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    const head = lines[i].match(new RegExp(`^(.*?)(${num})\\s*([+\\-−])\\s*(${num})(?:\\s+\\2)?\\s*$`));
    const mid = lines[i + 1] && lines[i + 1].match(new RegExp(`^\\s*[•*+\\-−]\\s*(${num})\\s*$`));
    const tail = lines[i + 2] && lines[i + 2].match(new RegExp(`^\\s*(${num})\\s*$`));
    if (head && mid && tail && mid[1] === head[4]) {
      const op = head[3] === '-' ? '−' : head[3];
      out.push(`${head[1]}${head[2]} ${op} ${head[4]} = ${tail[1]}`);
      i += 2;
      continue;
    }
    out.push(lines[i]);
  }
  return out.join('\n');
}

/**
 * Readable maths for a rubric. Leaves ordinary prose, Urdu, and prices alone.
 */
export function toPlainMath(text) {
  const original = dropStraySumMarks(showLatexUnderlines(normalizeMathGlyphs(text)));
  if (!hasLatex(original) && !original.includes('$')) {
    return stackCrossedDivision(clarifyWorkedSums(dropComparisonStars(cleanPaperDecor(fencePlainWorking(original)))));
  }

  let s = rewriteArrays(original);
  s = hasLatex(s) ? applyPowers(s) : s;
  s = applyFractions(s);
  s = s.replace(/\\sqrt\s*\{([^{}]*)\}/g, (_, inner) => {
    const v = inner.trim();
    return isSimple(v) ? `√${v}` : `√(${v})`;
  });
  s = s.replace(/\\(?:text|mathrm|mathbf)\s*\{([^{}]*)\}/g, '$1');
  SYMBOLS.forEach(([re, glyph]) => {
    s = s.replace(re, glyph);
  });
  s = s.replace(/\\(?:left|right|bigl|bigr|Bigl|Bigr)\b/g, '');
  s = s.replace(/\\[a-zA-Z]+\*?/g, '');
  s = s.replace(/\$([^$\n]+)\$/g, (full, inner) => (
    isWrappedMath(inner) ? inner.trim() : full
  ));
  s = s.replace(/\$\$/g, '');
  s = s.replace(/[{}]/g, '');
  s = s.replace(/[^\S\n]{2,}/g, ' ');
  return stackCrossedDivision(clarifyWorkedSums(dropComparisonStars(cleanPaperDecor(fencePlainWorking(rewriteDivisionLines(unwrapDollarWorking(s)))))));
}
